/**
 * RP4 — Grip-drag state machine extracted from `Scene3DStage.tsx`.
 *
 * Drives the minimum-viable grip drag loop:
 *
 * 1. `onGripStart(entityId, kind, world, grip, event)` → seed the drag
 *    ref, set pointer capture if available, install window-level
 *    pointermove + pointerup + pointercancel + lostpointercapture +
 *    blur + keydown listeners.
 * 2. pointermove → project client → world via the camera adapter,
 *    invoke `onGripDrag(entityId, …)` so the upstream reducer can
 *    update the entity preview live.
 * 3. pointerup — TWO outcomes (AutoCAD parity, GRIPS-INTERACTION PR-1):
 *    - the pointer MOVED past the click threshold (a press-DRAG) → commit
 *      (close the grouped-undo batch) and tear down window listeners.
 *    - the pointer did NOT move (a CLICK) → ARM the grip: enter the modal
 *      "** STRETCH **" phase (`onGripArm`), keep the batch OPEN, and wait for
 *      the SECOND click to specify the stretch point. The next pointerdown
 *      anywhere commits at that point through the SAME commit path, so
 *      arm→commit is ONE grouped-undo entry.
 * 4. Escape / pointercancel / lostpointercapture / window blur during a
 *    drag OR the armed phase → ABORT (revert the in-progress batch,
 *    listeners removed, armed model cleared, cursor restored).
 *
 * # Robustness (Codex P1-A)
 *
 * The grouped-undo batch MUST always be closed exactly once. A drag that
 * ends via pointerup commits it; any abnormal termination
 * (`pointercancel`, lost pointer-capture, window blur, React unmount
 * mid-drag) aborts it. Without this an open batch poisons EVERY
 * subsequent dispatch into one never-closing group. The teardown +
 * batch-close are funnelled through a single IDEMPOTENT `finish(mode)`
 * so a double-fire (e.g. pointerup AND lostpointercapture for the same
 * gesture) closes the batch once. Re-entrancy: a second pointerdown
 * before the first ends aborts the prior drag first, so two batches are
 * never open at once. The caller wires the returned `cleanup` into a
 * React `useEffect` return so unmount aborts an active drag.
 *
 * # AutoCAD parity
 *
 * Hot-grip CLICK-ARM mode (single-click arms a grip, second click commits the
 * stretch) SHIPS here (GRIPS-INTERACTION PR-1) alongside the original
 * press-drag. GRIPS-INTERACTION PR-2 (Ctrl→Enter/Space correction, §8.2) adds
 * the modal edit-mode cycle: pressing Enter or Spacebar WHILE ARMED cycles
 * Stretch → Move → Rotate → Scale → Mirror → (wraps), source-verified against
 * Autodesk's docs (NOT Ctrl — see `gripInteractionConstants.ts`'s
 * `GRIP_MODE_LABELS` doc comment for the citation + correction). The cycled
 * mode is read from `hotGripStore.getArmedEditModeSnapshot()` at commit time;
 * a non-Stretch commit dispatches through `gripModeTransform.ts`'s
 * `buildGripTransformCommand` (`onGripTransformCommit`) instead of the
 * Stretch pseudo-property path (`onGripDragProp`).
 */

import * as THREE from "three";
import * as React from "react";

import type { Grip, GripKind } from "../grips/computeGrips";
import {
  GRIP_ARMED_CURSOR,
  GRIP_CLICK_ARM_DRAG_THRESHOLD_PX,
  type GripEditMode,
} from "../grips/gripInteractionConstants";
import { getArmedEditModeSnapshot } from "../grips/hotGripStore";
import type { Scene3DStageProps } from "../Scene3DStageProps";
import { unapplyOffset } from "../coordinateOrigin";
import { resolveObjectSnap, AUTOSNAP_MAGNET } from "../snap";
import { MIN_ZOOM, type OsnapBindings } from "../stageInteractions";
import type { SpatialIndex } from "../types";
import { isSysvarOverrideHotkey } from "../../dynamicInput/sysvarHotkeys";
import type { GripTransformEditMode } from "../../state/gripModeTransform";

interface GripDragState {
  entityId: string;
  grip: Grip;
}

interface BuildGripStartHandlerArgs {
  liveCameraRef: { current: THREE.Camera | null };
  overlayShellRef: { current: HTMLDivElement | null };
  onGripDragProp: Scene3DStageProps["onGripDrag"];
  /**
   * GRIPS-SUBSYSTEM (§5.11) — undo coalescing. A grip drag fires
   * `onGripDrag` (→ `properties/apply`) on EVERY pointermove, so without
   * grouping the now-invertible apply would push one history entry per
   * move. These open / close a grouped-undo batch (the repo's
   * `history/begin-batch` … `history/commit-batch` convention) so ONE
   * Ctrl+Z reverts the whole drag to its pre-drag geometry. `onCancel`
   * aborts the batch (Escape) — reverting the in-progress drag.
   */
  onGripDragStart?: Scene3DStageProps["onGripDragStart"];
  onGripDragEnd?: Scene3DStageProps["onGripDragEnd"];
  onGripDragCancel?: Scene3DStageProps["onGripDragCancel"];
  /**
   * GRIPS-INTERACTION PR-1 (§8.2 / §5.11) — click-arm state notifications. A
   * CLICK on a grip (press + release WITHOUT crossing
   * {@link GRIP_CLICK_ARM_DRAG_THRESHOLD_PX}) ARMS it: `onGripArm(grip)` fires
   * so the host warms the typed armed-grip model (`hotGripStore.setArmedGrip`)
   * → GRIPHOT recolour + armed indicator, and the grip enters the modal
   * "specify stretch point" phase. `onGripDisarm()` fires when the interaction
   * leaves the armed phase (second-click commit OR Escape/abort) so the host
   * clears the armed model. OPTIONAL — omitting them keeps the pre-PR-1
   * press-drag-only behaviour (the arm phase still works, it just notifies no
   * one, so no visual arm indicator paints).
   */
  onGripArm?: (grip: Grip) => void;
  onGripDisarm?: () => void;
  /**
   * GRIPS-INTERACTION PR-2 — fires when Enter/Space cycles the armed grip's
   * edit mode. The gesture stays OPEN (no `finish` call) — only the mode
   * changes; the host (`hotGripStore.cycleArmedEditMode`) owns the mode
   * state, this handler just relays the keypress. OPTIONAL — omitting it
   * means Enter/Space fall through to the existing "ANY key aborts" rule
   * below (pre-PR-2 behaviour: the modal prompt has no mode to cycle to).
   */
  onGripCycleMode?: () => void;
  /**
   * GRIPS-INTERACTION PR-2 — fires on the armed-commit click when the
   * CURRENT armed mode (`hotGripStore.getArmedEditModeSnapshot()`) is NOT
   * "stretch". `entityIds` is the live selection at commit time (see
   * `selectedIdsRef` below), `basePoint` is the armed grip's own world
   * position, `targetPoint` the resolved commit point — both TRUE
   * document/WCS coords, matching what the Stretch path already dispatches.
   * OPTIONAL — omitting it while the mode is non-Stretch simply drops the
   * commit (no dispatch), same "notifies no one" contract as `onGripArm`.
   */
  onGripTransformCommit?: (
    mode: GripTransformEditMode,
    entityIds: readonly string[],
    basePoint: { x: number; y: number },
    targetPoint: { x: number; y: number },
  ) => void;
  /**
   * GRIPS-INTERACTION PR-2 — fires exactly once at the TOP of `onArmedCommit`
   * (i.e. `armed === true`, a genuine click-arm commit — never a plain
   * press-drag, which commits through a different path that never installs
   * `onArmedCommit` at all), for EVERY mode including Stretch. Carries just
   * the final cycled mode so the host can echo the completion line ("summary.
   * <mode>") to the command line without needing to separately reconcile
   * `onGripDragEnd` (fires for both plain drags AND armed-Stretch commits —
   * ambiguous on its own) against the armed state. OPTIONAL — omitting it
   * means no completion echo, zero other behaviour change.
   */
  onGripArmedCommit?: (mode: GripEditMode) => void;
  gripDragRef: { current: GripDragState | null };
  /**
   * Codex P1 (#317) — STABLE cross-render carrier for the currently-active
   * drag's teardown. The handler bundle is rebuilt whenever a callback prop
   * changes identity (e.g. inline App.tsx callbacks re-created by the
   * `properties/apply` re-render the FIRST pointermove triggers). If the
   * active-drag teardown lived in the per-build closure, that rebuild would
   * (a) strand the old drag and (b) make the unmount cleanup effect's dep
   * churn so it fires `abort` MID-DRAG. Holding `activeFinish` in this caller-
   * owned ref keeps the in-progress drag alive across rebuilds, and lets the
   * abort-on-unmount effect read the latest teardown without depending on the
   * bundle identity.
   */
  activeFinishRef: {
    current: ((mode: "commit" | "abort") => void) | null;
  };
  /**
   * GRIPS-INTERACTION (re-audit P2) — live OSNAP bindings (OSMODE / APERTURE /
   * AUTOSNAP), the SAME ref the live hover/pick path consumes
   * (`Scene3DStage` → `useOsnapBindings`). When present + the AUTOSNAP magnet
   * bit is on + a running-snap candidate sits under the cursor, the dispatched
   * grip coordinate is CLAMPED to the snap point (AutoCAD parity: a grip drag
   * honours running object snaps exactly like normal cursor input). OPTIONAL so
   * existing call-sites / tests that omit it keep the raw-cursor behaviour
   * (snapping simply inactive) — zero behaviour change.
   */
  osnapBindingsRef?: { current: OsnapBindings | null };
  /**
   * GRIPS-INTERACTION (re-audit P2) — live spatial index (the same
   * `spatialIndexRefForHover` the hover path queries). Required alongside
   * `osnapBindingsRef` for in-drag snapping; OPTIONAL for the same
   * backward-compat reason as above.
   */
  spatialIndexRef?: { current: SpatialIndex | null };
  /**
   * #350 @codex — the active-space id (caller keeps `.current` fresh). The
   * AUTHORITATIVE dragged-entity space for the same-space snap filter when the
   * dragged entity is NOT in the spatial index (a flattened Block INSERT pushes
   * only its child renderables — `getEntityById(draggedId)` is undefined), so
   * the index lookup alone can't recover the space. OPTIONAL (backward-compat).
   */
  activeSpaceIdRef?: { current: string };
  /**
   * GRIPS-INTERACTION PR-2 — the LIVE selection (mirrors the
   * `activeSpaceIdRef` / `osnapBindingsRef` ref-injection pattern above: read
   * fresh at commit time via `.current`, never captured in a closure at arm
   * time — the same staleness class the Codex P1 #317 `activeFinishRef`
   * comment above warns about). MOVE/ROTATE/SCALE/MIRROR act on the WHOLE
   * selection (AutoCAD parity — "Editing multiple grip points"), not just the
   * armed grip's own entity, so a stale captured id list would silently
   * transform the WRONG set if the selection changed between arm and commit.
   * OPTIONAL — omitted callers fall back to `[draggedEntityId]` (see
   * `onArmedCommit`), a safe single-entity default.
   */
  selectedIdsRef?: { current: readonly string[] };
}

/** Build-handler return: the start handler + an unmount-time abort hook. */
export interface GripStartHandlerBundle {
  handleGripStart: (
    entityId: string,
    gripKind: GripKind,
    world: { x: number; y: number },
    grip: Grip,
    event: React.PointerEvent<HTMLDivElement>,
  ) => void;
  /**
   * Abort an in-progress drag (no-op when idle). Wire into a React
   * `useEffect` cleanup so unmounting mid-drag closes the open
   * grouped-undo batch (aborts it) instead of leaving it dangling.
   */
  cleanup: () => void;
}

/**
 * Build the grip start handler. Returns `{ handleGripStart, cleanup }` —
 * caller `useMemo`-wraps the bundle and wires `cleanup` into a
 * `useEffect` return so React unmount aborts any active drag.
 */
export function buildGripStartHandler(
  args: BuildGripStartHandlerArgs,
): GripStartHandlerBundle {
  const {
    liveCameraRef,
    overlayShellRef,
    onGripDragProp,
    onGripDragStart,
    onGripDragEnd,
    onGripDragCancel,
    onGripArm,
    onGripDisarm,
    onGripCycleMode,
    onGripTransformCommit,
    onGripArmedCommit,
    gripDragRef,
    activeFinishRef,
    osnapBindingsRef,
    spatialIndexRef,
    activeSpaceIdRef,
    selectedIdsRef,
  } = args;

  // The teardown for the CURRENTLY-active drag lives in the caller-owned
  // `activeFinishRef` (`null` when idle), NOT in this build closure — so a
  // bundle rebuild (callback-prop identity change mid-drag) does NOT strand
  // the active drag (Codex P1 #317). A single funnel (`finish`) replaces the
  // four near-identical inline teardowns the prior code had — guaranteeing
  // the grouped-undo batch is closed exactly once regardless of HOW the drag
  // ends.

  /**
   * GRIPS-INTERACTION (re-audit P2) — resolve a running OSNAP candidate for the
   * dragged grip and, when the AUTOSNAP magnet bit is on, return the snap point;
   * otherwise return the raw cursor unchanged. Mirrors the magnet branch of the
   * live `pointerSnapStep` (incl. WRAPPING `getEntityById`), with one grip-drag
   * specific: the entity BEING dragged is excluded from snap acquisition.
   *
   * Why exclude the dragged entity: the spatial index is rebuilt only on an
   * entities-identity change (a `useEffect`), NOT per pointermove, so the dragged
   * entity sits in the index at its PRE-drag geometry for the whole gesture.
   * Without the exclusion (a) the first sub-aperture move snaps back onto the
   * grip's own old vertex → the grip is un-nudgeable until the cursor jumps past
   * the aperture, and (b) a Start grip snaps onto the SAME object's End vertex,
   * collapsing it. AutoCAD excludes the stretched object from running-osnap
   * acquisition; we drop the whole entity via the getter (resolveObjectSnap
   * skips ids whose getter returns `undefined` — the same mechanism
   * `pointerSnapStep` relies on for its space/frozen filter).
   *
   * @param draggedId entity id of the grip being dragged — excluded from snap.
   * @param cursorOffsetWorld cursor in OFFSET-WORLD (`cam.unproject` result —
   *   the SAME frame the spatial index + `resolveObjectSnap` use, see
   *   osnapTypes.ts). The returned point is also OFFSET-WORLD, so the caller
   *   maps it back through `unapplyOffset` for the document-frame dispatch.
   * @param cursorClient raw `e.clientX/Y` — passed through to the resolver for
   *   the input shape (the resolver math works in WORLD units; `cursorClient`
   *   is carried but not consumed by `resolveObjectSnap`, same as the live
   *   `pointerSnapStep` passes it through).
   * @param canvasWidthPx canvas CSS width (`rect.width`) for the ppw derivation.
   */
  const resolveGripSnap = (
    draggedId: string,
    cursorOffsetWorld: { x: number; y: number },
    cursorClient: { x: number; y: number },
    cam: THREE.Camera,
    canvasWidthPx: number,
  ): { x: number; y: number } => {
    const osnap = osnapBindingsRef?.current ?? null;
    const spatialIndex = spatialIndexRef?.current ?? null;
    if (!osnap || !spatialIndex || osnap.activeModes.size === 0) {
      return cursorOffsetWorld;
    }
    // Magnet gate (AUTOSNAP bit 4): without it AutoCAD draws the marker but does
    // NOT clamp the point, so the dispatched coord stays the raw cursor.
    const magnetOn = (osnap.autosnap & AUTOSNAP_MAGNET) !== 0;
    if (!magnetOn) return cursorOffsetWorld;
    // `pixelsPerWorld` for the aperture — derived from the live ORTHO camera the
    // SAME way the live snap path computes its paper ppw
    // (`stageInteractions.resolveInputSpaceAt`: `zoom × canvasW / (right − left)`).
    // Non-ortho cameras (none in this 2D editor) can't produce this ppw, so snap
    // is skipped (raw cursor) — defensive, never a wrong-scale aperture.
    if (!(cam instanceof THREE.OrthographicCamera)) return cursorOffsetWorld;
    const frustumW = Math.max(cam.right - cam.left, 1e-12);
    const pixelsPerWorld =
      (Math.max(cam.zoom, MIN_ZOOM) * Math.max(canvasWidthPx, 1)) / frustumW;
    // Active-space eligibility (#350 @codex P1). The shared spatial index mixes
    // paper + model entities (a layout's bulk-load appends model-space entities),
    // so a grip snap MUST only consider candidates in the dragged entity's OWN
    // space — else a paper/layout grip could magnet to invisible model geometry
    // with overlapping coords (or vice versa) and dispatch a foreign-space point
    // as the dragged entity's new coordinate. Mirrors how pointerSnapStep wraps
    // getEntityById with the candidate-eligibility predicate. (VP-frozen-layer
    // filtering is part of the still-deferred in-VP grip-snap routing — the grip
    // cursor itself does not space-route yet.)
    // Prefer the index lookup; fall back to the active-space ref when the
    // dragged entity is absent from the index (a flattened Block INSERT — only
    // its `${id}::...` children are indexed). #350 @codex.
    const draggedSpaceId =
      osnap.getEntityById(draggedId)?.spaceId ?? activeSpaceIdRef?.current;
    const hit = resolveObjectSnap({
      cursorWorld: cursorOffsetWorld,
      cursorClient,
      pixelsPerWorld,
      aperturePx: osnap.aperturePx,
      activeModes: osnap.activeModes,
      spatialIndex,
      getEntityById: (id) => {
        // Exclude the dragged entity AND its flattened block-insert CHILDREN
        // (#350 @codex P1/P2). `flattenBlockInsertEntity` pushes a block
        // insert's child renderables into the index as `${insertId}::...`, so an
        // exact-id check alone lets a block-insert grip snap back to its own
        // pre-drag child geometry (the self-stick this guard exists to prevent).
        if (id === draggedId || id.startsWith(`${draggedId}::`)) return undefined;
        const entity = osnap.getEntityById(id);
        if (!entity) return undefined;
        // Same-space-only (paper↔model guard) — drop cross-space candidates.
        if (draggedSpaceId !== undefined && entity.spaceId !== draggedSpaceId) {
          return undefined;
        }
        return entity;
      },
    });
    // No candidate under the cursor → raw cursor (zero behaviour change).
    return hit ? hit.world : cursorOffsetWorld;
  };

  function handleGripStart(
    entityId: string,
    _gripKind: GripKind,
    _world: { x: number; y: number },
    grip: Grip,
    event: React.PointerEvent<HTMLDivElement>,
  ): void {
    if (!onGripDragProp) return;

    // Re-entrancy guard: a second pointerdown before the previous drag
    // ended must NOT open a second batch. Abort the prior drag first so
    // exactly one batch is ever open.
    if (activeFinishRef.current) {
      activeFinishRef.current("abort");
    }

    gripDragRef.current = { entityId, grip };
    // Open the grouped-undo batch BEFORE any pointermove fires a commit so
    // every move-driven `properties/apply` accumulates into one entry. For the
    // click-arm path the batch spans arm→commit so the whole stretch is ONE
    // Ctrl+Z (the second-click commit re-uses this same open batch).
    onGripDragStart?.();
    try {
      (event.target as HTMLElement).setPointerCapture(event.pointerId);
    } catch {
      /* jsdom may not implement setPointerCapture */
    }

    // Click-vs-drag discrimination (GRIPS-INTERACTION PR-1). `armed` = the grip
    // is in the modal "specify stretch point" phase; `moved` = the press has
    // crossed the drag threshold (so pointerup commits rather than arms).
    const downClient = { x: event.clientX, y: event.clientY };
    let moved = false;
    let armed = false;
    // Codex P2 — a Shift-click seeds the ADDITIVE multi-hot-grip path:
    // `gripsOverlay`'s `onPointerDown` already called `onGripActivate(grip,
    // event.shiftKey)` (BEFORE this handler runs) to add the grip to the hot
    // set via `setHotGrip(ref, additive)`. AutoCAD's Shift-click is a discrete
    // "add this grip to the set" gesture — it must NOT also arm the modal
    // stretch on release; that lets the user keep Shift-clicking more grips
    // to build the set. Captured once at press time (the modifier held for
    // THIS gesture), consumed in `onUp` below.
    const additive = event.shiftKey;
    // GRIPS-INTERACTION PR-1 (Codex P3) — the stage-shell cursor value that was
    // in effect BEFORE the armed phase overrode it. The central cursor policy
    // (`Scene3DStage`) parks the shell at `none` while the crosshair overlay is
    // up; disarming must RESTORE that value — NOT blank it to `""`, which would
    // surface the browser arrow over the drawing until the next hover/leave
    // transition re-runs the central policy.
    let prevShellCursor = "";

    // Shared pointer → document-frame RESOLUTION + (usually) dispatch. Used by
    // BOTH the press-drag live move and the armed-phase live preview +
    // second-click commit, so the click-arm commit lands on the EXACT
    // coordinate the drag path would (one commit path — AutoCAD parity, ONE
    // grouped-undo entry).
    //
    // GRIPS-INTERACTION PR-2 — `dispatchStretch` (default true) gates the
    // STRETCH pseudo-property dispatch (`onGripDragProp`) so the
    // non-Stretch armed-commit branch below can reuse the SAME
    // client→NDC→unproject→snap→unapplyOffset resolution (never duplicated)
    // without firing a bogus Stretch reshape. The resolved TRUE-WCS point is
    // always returned (`undefined` only when the camera/canvas aren't ready),
    // so the caller can build a MOVE/ROTATE/SCALE/MIRROR command from it.
    const applyPointerTo = (
      clientX: number,
      clientY: number,
      dispatchStretch = true,
    ): { x: number; y: number } | undefined => {
      const drag = gripDragRef.current;
      if (!drag) return undefined;
      const cam = liveCameraRef.current;
      const canvas =
        overlayShellRef.current?.querySelector(
          "canvas",
        ) as HTMLCanvasElement | null;
      if (!cam || !canvas) return undefined;
      const rect = canvas.getBoundingClientRect();
      const ndcX = ((clientX - rect.left) / Math.max(rect.width, 1)) * 2 - 1;
      const ndcY = -(((clientY - rect.top) / Math.max(rect.height, 1)) * 2 - 1);
      // `cam.unproject` yields an OFFSET-WORLD coordinate (the scene renders
      // every DXF coord MINUS the locked `coordinateOrigin` so Float32 buffers
      // stay precision-safe — raycast/distance.ts + cameraSnapshot.ts state the
      // same offset-world contract; ~30 adapters call `applyOffset`). The grip
      // patch consumed downstream (`gripDragTranslate` → `Start X` / `Insertion X`
      // / … on the geometry DOCUMENT) is in TRUE DXF/world coords, so for a
      // non-zero `coordinateOrigin` (geodetic / UTM imports, `setCoordinateOrigin`)
      // dispatching the raw offset-world value would write a coordinate off by the
      // origin (~1e6) — corrupting the raw document + DXF export. Map back to the
      // document frame via `unapplyOffset` (the inverse of `applyOffset` the
      // adapters use) BEFORE dispatching. No-op when no origin is set.
      const v = new THREE.Vector3(ndcX, ndcY, 0).unproject(cam);
      // GRIPS-INTERACTION (re-audit P2) — clamp the dragged coordinate to a
      // running OSNAP candidate, mirroring the live hover/pick snap step
      // (`snap/pointerSnapStep.ts`). `v` is in OFFSET-WORLD (the camera
      // `unproject` returns DXF − `coordinateOrigin`; `clientToWorld` in
      // stageInteractions.ts returns the same un-offset value, and the spatial
      // index + `resolveObjectSnap` operate in that frame — osnapTypes.ts:
      // "`world` is the projected snap target in offset-world space"). So we
      // resolve the snap in OFFSET-WORLD, then map `hit.world` back through
      // `unapplyOffset` to TRUE document coords for dispatch — identical to the
      // raw-cursor path, just with `v` possibly replaced by the snap point.
      //
      // Magnet gate: only replace the cursor with the snap point when the
      // AUTOSNAP magnet bit is set (exactly like `pointerSnapStep`). When no
      // bindings / empty modes / no hit / magnet off → `snapped` stays `v`, so
      // the dispatched coord is the unchanged raw cursor (zero behaviour change).
      //
      // SCOPE — identity / model-space common case only (the existing grip drag
      // does NOT space-route either). In MSPACE_VIA_VP (cursor over an unlocked
      // active viewport) the snap would need routing through
      // `resolveActiveInputSpace` so the query runs in the VP's MODEL space, as
      // the hover path does. Follow-up: in-VP grip snap via resolveActiveInputSpace.
      const snapped = resolveGripSnap(
        drag.entityId,
        { x: v.x, y: v.y },
        { x: clientX, y: clientY },
        cam,
        rect.width,
      );
      // z carried only for the unapplyOffset shape; the dispatch below sends
      // X/Y only and the reducer preserves the entity's own Z (snap is 2D).
      const world = unapplyOffset({ x: snapped.x, y: snapped.y, z: v.z });
      if (dispatchStretch) {
        onGripDragProp(drag.entityId, {
          gripDragWorldX: String(world.x),
          gripDragWorldY: String(world.y),
          gripId: drag.grip.id,
          gripKind: drag.grip.kind,
        });
      }
      return { x: world.x, y: world.y };
    };

    const onMove = (e: PointerEvent): void => {
      if (!gripDragRef.current) return;
      // Press phase: ignore sub-threshold jitter so a bare CLICK arms instead
      // of committing a zero-length stretch. Once armed, every move updates the
      // live preview (the entity rubber-bands to follow the cursor).
      if (!armed && !moved) {
        const dx = e.clientX - downClient.x;
        const dy = e.clientY - downClient.y;
        const t = GRIP_CLICK_ARM_DRAG_THRESHOLD_PX;
        if (dx * dx + dy * dy < t * t) return;
        moved = true;
      }
      // GRIPS-INTERACTION PR-2 — while ARMED in a non-Stretch mode, the live
      // pointer preview must NOT dispatch a STRETCH pseudo-property patch (it
      // would visually mis-preview a reshape instead of a move/rotate/scale/
      // mirror). No replacement live preview is drawn either — a documented,
      // PRE-EXISTING fidelity gap shared with the click-based MOVE/ROTATE/
      // SCALE/MIRROR draft commands (`draftModifyStateMachine.ts`), which
      // likewise render no live preview during their own base→destination
      // click steps. Follow-up: `GRIPS-MODIFY-LIVE-PREVIEW`.
      if (armed && getArmedEditModeSnapshot() !== "stretch") return;
      applyPointerTo(e.clientX, e.clientY);
    };

    // Press-phase pointerup: a real DRAG (moved) commits; a CLICK (no move)
    // ARMS the grip (modal stretch phase). Armed-phase pointerups are ignored —
    // the second CLICK's pointerdown is the commit (onArmedCommit).
    const onUp = (e: PointerEvent): void => {
      if (armed) return;
      // Release-only drag (Codex P2): some browsers / test harnesses deliver the
      // final `pointerup` WITHOUT an intervening `pointermove`, so `moved` (set
      // only in `onMove`) stays false even though the pointer travelled past the
      // drag threshold. Re-evaluate the threshold against the RELEASE coords so a
      // genuine press-drag-release COMMITS (and applies its final destination)
      // instead of falsely arming and stranding the grouped-undo batch open.
      if (!moved && Number.isFinite(e.clientX) && Number.isFinite(e.clientY)) {
        const dx = e.clientX - downClient.x;
        const dy = e.clientY - downClient.y;
        const t = GRIP_CLICK_ARM_DRAG_THRESHOLD_PX;
        if (dx * dx + dy * dy >= t * t) {
          moved = true;
          // No live move ran for this gesture, so the entity was never nudged —
          // apply the final destination now so the committed batch reflects the
          // drag (else `finish("commit")` would close an EMPTY batch).
          applyPointerTo(e.clientX, e.clientY);
        }
      }
      if (moved) {
        finish("commit");
        return;
      }
      // Codex P2 — an additive (Shift-click) no-move release only toggles hot-
      // grip membership (already applied via `onGripActivate` before this
      // handler ran); it must NOT arm the modal stretch, else the user's NEXT
      // Shift-click on another grip would be consumed as the stretch commit
      // instead of continuing to build the multi-hot set. The batch this press
      // opened never received a dispatch (no move happened), so aborting it is
      // a clean no-op close — same path the outside-canvas guard uses.
      if (additive) {
        finish("abort");
        return;
      }
      arm();
    };

    // Enter the modal "specify stretch point" phase. The batch STAYS open so
    // arm→commit is ONE grouped-undo entry (reuses the drag commit path).
    const arm = (): void => {
      armed = true;
      onGripArm?.(grip);
      const shell = overlayShellRef.current;
      if (shell?.style) {
        // Capture the pre-arm cursor so disarm can RESTORE it (Codex P3) rather
        // than blank it — see `prevShellCursor` + the `finish` armed branch.
        prevShellCursor = shell.style.cursor;
        shell.style.cursor = GRIP_ARMED_CURSOR;
      }
      // The next pointerdown ANYWHERE commits at that point. CAPTURE phase so it
      // fires BEFORE the grip glyph's React handler → a second click that lands
      // on a grip commits (does NOT re-arm); stopPropagation stops it reaching
      // the overlay / selection handlers (the pick is consumed by the stretch).
      window.addEventListener("pointerdown", onArmedCommit, true);
    };

    const onArmedCommit = (e: PointerEvent): void => {
      if (e.button !== 0) {
        // Codex P2 — a non-primary (right/middle) click while armed most
        // often opens app UI (the stage context menu). Previously this just
        // `return`ed, leaving the armed edit — and this CAPTURE-phase
        // pointerdown listener — installed; the menu's OWN subsequent left-
        // click on a menu item then landed inside the canvas rect (menus
        // render on top of the canvas) and was swallowed as the stretch
        // commit point instead of activating the item. Abort instead, so the
        // listener is torn down before that left-click can reach it —
        // AutoCAD parity: opening app UI cancels a pending grip pick.
        finish("abort");
        return;
      }
      // Restrict the armed COMMIT to the drawing stage (Codex P2). This capture
      // handler sits on `window`, so a primary pointerdown on app chrome (sidebar
      // / toolbar / palette) would otherwise be swallowed and its coords projected
      // through the canvas rect — committing a bogus stretch instead of letting
      // the clicked UI activate. Only a pick INSIDE the canvas specifies the
      // stretch point; a click positively OUTSIDE it ABORTS the armed edit (which
      // closes the open grouped-undo batch) and is left UN-consumed so the UI
      // element still receives it — AutoCAD parity (touching UI cancels a pending
      // grip pick). When the canvas rect is unavailable the projection can't run
      // anyway, so we don't reject — the commit no-ops safely.
      const canvas = overlayShellRef.current?.querySelector(
        "canvas",
      ) as HTMLCanvasElement | null;
      const rect = canvas?.getBoundingClientRect();
      if (
        rect &&
        (e.clientX < rect.left ||
          e.clientX > rect.right ||
          e.clientY < rect.top ||
          e.clientY > rect.bottom)
      ) {
        // Outside the stage → cancel the armed edit; do NOT preventDefault /
        // stopPropagation so the clicked UI element still receives the event.
        finish("abort");
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      // GRIPS-INTERACTION PR-2 — branch on the CURRENT armed mode (read fresh
      // here, not captured at arm time — the mode can have cycled via
      // Enter/Space any number of times since `arm()` ran).
      const mode = getArmedEditModeSnapshot();
      // Fires for EVERY mode (including Stretch) — this handler ONLY ever
      // runs for a genuine click-arm commit (see the field doc comment), so
      // it is unambiguous by construction, unlike `onGripDragEnd` below.
      onGripArmedCommit?.(mode);
      if (mode === "stretch") {
        // Unchanged PR-1 behaviour: STRETCH dispatches the pseudo-property
        // patch from inside `applyPointerTo` itself.
        applyPointerTo(e.clientX, e.clientY); // final destination = the pick point
      } else {
        // Non-Stretch commit: resolve the SAME TRUE-WCS point the Stretch
        // path would (client → NDC → unproject → snap → unapplyOffset), but
        // suppress the Stretch pseudo-property dispatch (`dispatchStretch =
        // false`) and instead build the MOVE/ROTATE/SCALE/MIRROR command from
        // it. `drag` is stable for the whole gesture (only cleared by
        // `finish`, which has not run yet), so `drag.entityId` is a safe
        // single-entity fallback when no `selectedIdsRef` was injected.
        const resolved = applyPointerTo(e.clientX, e.clientY, false);
        const drag = gripDragRef.current;
        if (resolved && drag) {
          const entityIds = selectedIdsRef?.current ?? [drag.entityId];
          onGripTransformCommit?.(mode, entityIds, grip.world, resolved);
        }
      }
      finish("commit");
    };

    const onCancel = (): void => finish("abort");
    const onLostCapture = (): void => {
      // The FIRST click's pointerup releases the implicit pointer capture,
      // firing lostpointercapture. In the armed phase we intentionally hold no
      // capture, so ignore it — only a genuine mid-DRAG capture steal aborts.
      if (armed) return;
      finish("abort");
    };
    const onBlur = (): void => finish("abort");
    // Codex rounds 2-4 — individually enumerating Escape, Delete, Ctrl+Z/Y,
    // then Enter, then Ctrl+S each kept surfacing ANOTHER global shortcut
    // that could run — mutate the selection, the history STACK, submit an
    // unrelated command, or export the document with uncommitted preview
    // geometry — INSIDE this gesture's still-open grouped-undo batch (armed
    // OR still mid-press-drag), because none of `useKeyboardShortcuts`'
    // global handlers know a grip edit is in flight. That is the SAME root
    // cause every round, so instead of continuing to enumerate individual
    // keys: abort on ANY keydown while this gesture is active — EXCEPT the
    // Shift-based temp OSNAP-override hotkeys (round-6 — bare Shift +
    // Shift+E/M/C/Q/P/T/D/A, `sysvarHotkeys.ts`), which this SAME gesture
    // already consumes LIVE via `resolveGripSnap`'s `osnapBindingsRef` — a
    // documented, already-shipped in-armed interaction, not a hypothetical
    // future one, so it is the one narrow, principled exception rather than
    // another enumerated shortcut; and — GRIPS-INTERACTION PR-2 — Enter /
    // Spacebar WHILE ARMED, which now CYCLE the modal edit mode instead of
    // aborting (source-verified against Autodesk's docs, NOT Ctrl — see
    // `gripInteractionConstants.ts`). This second exception is intentionally
    // NARROW too: Enter/Space only cycle the mode when a grip is ALREADY
    // armed (`armed === true`); mid-press-drag (not yet armed) Enter/Space
    // still fall through to the general abort rule below, matching AutoCAD
    // (Enter/Space only mean something once inside the modal grip prompt).
    // AutoCAD parity otherwise: almost any keyboard input while inside an
    // active modal grip prompt, "** STRETCH **\nSpecify stretch point:",
    // exits it. Installed in the CAPTURE phase (see `addEventListener`
    // below) so it always runs BEFORE the bubble-phase global shortcut
    // handler, deterministically — mirroring `onArmedCommit`'s capture-phase
    // pointerdown guard above. We do NOT preventDefault/stopPropagation on
    // the abort path: the grip edit is only ABORTED (closing its batch),
    // then the key keeps propagating so whatever global handler owns it
    // still runs normally. The cycle path DOES preventDefault/stopPropagation
    // (see below) — it must win the race against `useKeyboardShortcuts`'
    // own bubble-phase Enter handler (repeat-last-command), which would
    // otherwise also fire for the same keypress.
    const onKey = (e: KeyboardEvent): void => {
      // Codex P2 — gate on `onGripCycleMode` being provided, matching the
      // field doc comment's promise: omitting it must fall through to the
      // general "ANY key aborts" rule below, not silently swallow Enter/
      // Space. Every REAL caller (Scene3DStage via useGripStartBundle)
      // always provides it; this only matters for a caller/test that omits
      // the optional param.
      if (armed && onGripCycleMode && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        e.stopPropagation();
        // Codex P1 — leaving STRETCH must revert any live stretch-preview
        // mutation already dispatched by `onMove` (its `armed &&
        // getArmedEditModeSnapshot() === "stretch"` live-dispatch branch
        // above fires a REAL `properties/apply` reshape on every
        // pointermove while armed in Stretch). Without this, cycling away
        // from Stretch leaves that mutation applied, and the eventual
        // MOVE/ROTATE/SCALE/MIRROR commit computes its delta/angle/factor
        // from the grip's ORIGINAL `grip.world` but applies it to the
        // ALREADY-STRETCH-MUTATED entity — stretched-then-transformed
        // instead of the clean transform the mode echo promised.
        //
        // `translateGripDrag` (STRETCH's pure math, `gripDragTranslate.ts`)
        // is a deterministic RECOMPUTATION from the dragged point for every
        // entity/grip-kind case — never an incremental delta — so
        // re-dispatching the SAME grip back to its OWN original world
        // position (captured once at arm time in `handleGripStart`'s
        // `grip` parameter, still in scope here) restores the pre-drag
        // geometry exactly. It is a harmless no-op when the pointer never
        // moved since arming (the "revert" target already equals the
        // current value).
        //
        // Checked fresh (`getArmedEditModeSnapshot()`) on EVERY keypress,
        // BEFORE `onGripCycleMode` advances the store — so a full
        // wrap-around (Stretch → … → Mirror → Stretch → cycle away again)
        // reverts again on that LATER transition too, not just the first.
        if (getArmedEditModeSnapshot() === "stretch") {
          const drag = gripDragRef.current;
          if (drag) {
            onGripDragProp(drag.entityId, {
              gripDragWorldX: String(grip.world.x),
              gripDragWorldY: String(grip.world.y),
              gripId: drag.grip.id,
              gripKind: drag.grip.kind,
            });
          }
        }
        onGripCycleMode();
        return; // gesture stays open — only the mode changed, not `finish`.
      }
      if (isSysvarOverrideHotkey(e)) return;
      finish("abort");
    };

    // IDEMPOTENT close. After the first call `activeFinish` is cleared, so
    // a duplicate event (pointerup + lostpointercapture for the same
    // gesture, blur racing pointercancel, …) is a no-op — the batch is
    // closed exactly once.
    const finish = (mode: "commit" | "abort"): void => {
      if (activeFinishRef.current !== finish) return;
      activeFinishRef.current = null;
      gripDragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("lostpointercapture", onLostCapture);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("pointerdown", onArmedCommit, true);
      if (armed) {
        // Leave the modal phase: clear the armed model + RESTORE the pre-arm
        // cursor (regardless of commit vs abort — both end the armed stretch).
        // Restoring the captured value (typically `none` while the crosshair
        // overlay is up) instead of blanking to `""` keeps the browser arrow
        // from flashing over the drawing until the central cursor policy's deps
        // next change (Codex P3).
        onGripDisarm?.();
        const shell = overlayShellRef.current;
        if (shell?.style) shell.style.cursor = prevShellCursor;
      }
      if (mode === "commit") {
        // Commit the grouped-undo batch — flattens every move-driven apply
        // into ONE history entry (one Ctrl+Z reverts the whole grip edit).
        onGripDragEnd?.();
      } else {
        // Abort the batch, reverting the in-progress edit to its pre-edit
        // geometry (AutoCAD Escape / lost-input-during-grip-edit parity).
        onGripDragCancel?.();
      }
    };

    activeFinishRef.current = finish;
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    // Abnormal terminations that the prior code ignored — any of these
    // leaving the batch open would poison every subsequent dispatch.
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("lostpointercapture", onLostCapture);
    window.addEventListener("blur", onBlur);
    // Codex P2 — CAPTURE phase (see `onKey` above) so the Delete guard always
    // wins the ordering race against `useKeyboardShortcuts`'s bubble-phase
    // window listener, regardless of which effect happened to mount first.
    window.addEventListener("keydown", onKey, true);
  }

  // React unmount mid-drag: abort the open batch (do not commit a
  // half-finished gesture). No-op when idle. Reads the active teardown via
  // the stable ref, so a caller can wire this into an UNMOUNT-ONLY effect
  // (empty deps) — a bundle rebuild no longer triggers a spurious abort.
  const cleanup = (): void => {
    if (activeFinishRef.current) activeFinishRef.current("abort");
  };

  return { handleGripStart, cleanup };
}

/**
 * Singleton ref shape for the drag state. Exported so the outer
 * Scene3DStage can declare it once via `useRef<GripDragState | null>`.
 */
export type { GripDragState };
